import { useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, DatePicker, Descriptions, Form, Input,
  InputNumber, Row, Select, Space, Switch, Tag, Typography,
} from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { workOrdersApi, sparesApi } from '@/features/maintenance/api'
import {
  spareProcurementApi,
  type CheckAndRaisePRResult,
} from '@/features/maintenance/spareProcurementApi'
import { machinesApi } from '@/features/masters/machines/api'
import { departmentsApi } from '@/features/masters/departments/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ProcurementStatusBadge from './components/ProcurementStatusBadge'

const { Text } = Typography

/**
 * Phase 3.2-B — "Raise PR from Work Order" form.
 *
 * Spec §3. User selects a WO + spare, types required qty. Everything
 * else (machine, department, projected stock, shortage, priority) is
 * auto-fetched. Submitting calls /spare-procurement/check-and-raise-pr.
 *
 * Result shows STOCK_OK / PR_RAISED / PR_EXISTS — never a fake number.
 */
export default function AutoPRFromWorkOrderPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [form] = Form.useForm()
  const [result, setResult] = useState<CheckAndRaisePRResult | null>(null)

  const woId = Form.useWatch('work_order_id', form) as string | undefined
  const spareId = Form.useWatch('spare_id', form) as string | undefined
  const requiredQty = Form.useWatch('required_qty', form) as number | undefined
  const directCharge = Form.useWatch('direct_charge', form) as boolean | undefined

  // Open work orders for the plant
  const { data: workOrders } = useQuery({
    queryKey: ['wo-open-for-pr', plantId],
    enabled: !!plantId,
    queryFn: () => workOrdersApi.list({
      plant_id: plantId, only_open: true, limit: 200,
    }),
  })
  const wo = (workOrders ?? []).find((w) => w.id === woId)

  const { data: machinesData } = useQuery({
    queryKey: ['machines-for-pr', plantId],
    enabled: !!plantId,
    queryFn: () => machinesApi.list({
      plant_id: plantId, is_active: true, page: 1, page_size: 500,
    }),
  })
  const machine = (machinesData?.items ?? []).find((m) => m.id === wo?.machine_id)

  const { data: deptsData } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
  })
  const dept = (deptsData?.items ?? []).find((d) => d.id === wo?.department_id)

  const { data: spares } = useQuery({
    queryKey: ['spares-for-pr'],
    queryFn: () => sparesApi.list({ limit: 2000 }),
  })
  const spare = (spares ?? []).find((s) => s.id === spareId)

  const shortageQty = useMemo(() => {
    if (!spare || requiredQty == null) return null
    const available = Number(spare.current_stock || 0)
    const shortage = Number(requiredQty) - available
    return shortage > 0 ? shortage : 0
  }, [spare, requiredQty])

  const suggestedPriority = useMemo(() => {
    if (!wo) return 'MEDIUM'
    if (wo.priority === 'CRITICAL' || wo.maintenance_type === 'EMERGENCY') return 'CRITICAL'
    if (directCharge) return 'HIGH'
    if (spare?.critical_spare) return 'HIGH'
    if (wo.priority === 'HIGH') return 'HIGH'
    return wo.priority || 'MEDIUM'
  }, [wo, spare, directCharge])

  const submit = useMutation({
    mutationFn: (vals: {
      work_order_id: string
      spare_id: string
      required_qty: number
      direct_charge?: boolean
      required_by?: dayjs.Dayjs
    }) => spareProcurementApi.checkAndRaisePR({
      work_order_id: vals.work_order_id,
      spare_id: vals.spare_id,
      required_qty: Number(vals.required_qty),
      direct_charge: !!vals.direct_charge,
      required_by: vals.required_by
        ? vals.required_by.format('YYYY-MM-DD') : undefined,
    }),
    onSuccess: (r) => {
      setResult(r)
      if (r.decision === 'PR_RAISED') {
        message.success(`Auto-PR ${r.pr_no} raised`)
      } else if (r.decision === 'PR_EXISTS') {
        message.info('A PR already exists for this work order + spare')
      } else {
        message.success('Stock is sufficient — no PR needed')
      }
    },
    onError: (e) => message.error((e as { message?: string })?.message ?? 'Failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => submit.mutate(v))

  return (
    <div>
      <PageHeader
        title="Raise PR from Work Order"
        subtitle="Check stock, auto-create PR if short. ENTER ONCE → AUTO FETCH → AUTO FLOW."
      />

      <Row gutter={12}>
        <Col xs={24} lg={14}>
          <Card title="Spare requirement">
            <Form form={form} layout="vertical">
              <Form.Item
                label="Work Order *"
                name="work_order_id"
                rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder="Select an open WO"
                  options={(workOrders ?? []).map((w) => ({
                    label: `${w.work_order_no} — ${w.priority} ${w.status}`,
                    value: w.id,
                  }))}
                  filterOption={(i, o) =>
                    (o?.label as string).toLowerCase().includes(i.toLowerCase())
                  }
                  onChange={() => setResult(null)}
                />
              </Form.Item>

              {wo && (
                <Descriptions size="small" bordered column={2}
                  style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Machine">
                    {machine
                      ? <Space>
                          <Text strong>{machine.machine_id_str}</Text>
                          <Text type="secondary">{machine.name}</Text>
                          <Tag>AUTO FETCHED</Tag>
                        </Space>
                      : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Department">
                    {dept?.name ?? '—'}{' '}
                    <Tag>AUTO FETCHED</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Priority">
                    <Tag color={wo.priority === 'CRITICAL' ? 'red'
                      : wo.priority === 'HIGH' ? 'orange'
                        : 'default'}>{wo.priority}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag>{wo.status}</Tag>
                  </Descriptions.Item>
                </Descriptions>
              )}

              <Form.Item
                label="Spare *"
                name="spare_id"
                rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder="Pick the spare"
                  options={(spares ?? []).map((s) => ({
                    label: `${s.spare_code} — ${s.spare_name}`,
                    value: s.id,
                  }))}
                  filterOption={(i, o) =>
                    (o?.label as string).toLowerCase().includes(i.toLowerCase())
                  }
                  onChange={() => setResult(null)}
                />
              </Form.Item>

              {spare && (
                <Descriptions size="small" bordered column={2}
                  style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Available Stock">
                    <Space>
                      <Text strong>{Number(spare.current_stock).toFixed(2)} {spare.uom}</Text>
                      <Tag>AUTO FETCHED</Tag>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Reorder Level">
                    {Number(spare.reorder_level).toFixed(2)} {spare.uom}
                  </Descriptions.Item>
                  <Descriptions.Item label="Average Rate">
                    ₹ {Number(spare.average_rate).toFixed(4)} <Tag>SYSTEM CALCULATED</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Critical?">
                    {spare.critical_spare ? <Tag color="orange">CRITICAL</Tag> : '—'}
                  </Descriptions.Item>
                </Descriptions>
              )}

              <Form.Item
                label="Required Qty *"
                name="required_qty"
                rules={[{ required: true, type: 'number', min: 0.001 }]}>
                <InputNumber
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                  onChange={() => setResult(null)}
                  addonAfter={spare?.uom ?? ''}
                />
              </Form.Item>

              {spare && shortageQty != null && (
                <Alert
                  type={shortageQty > 0 ? 'warning' : 'success'}
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={shortageQty > 0
                    ? `Shortage = ${shortageQty.toFixed(2)} ${spare.uom}. Auto-PR will be raised on submit.`
                    : 'Stock is sufficient. No PR will be raised.'}
                />
              )}

              <Form.Item
                label="Direct charge to work order?"
                name="direct_charge"
                valuePropName="checked"
                extra="Charge the spare cost straight to the WO without stocking it. Always raises a PR.">
                <Switch />
              </Form.Item>

              <Form.Item label="Required By" name="required_by">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item label="Priority (auto-suggested)">
                <Tag color={suggestedPriority === 'CRITICAL' ? 'red'
                  : suggestedPriority === 'HIGH' ? 'orange'
                    : 'default'}>{suggestedPriority}</Tag>
                <Tag>AUTO FETCHED</Tag>
              </Form.Item>

              <Form.Item label="Reason / Remarks" name="reason">
                <Input.TextArea rows={2} />
              </Form.Item>

              <Button type="primary" onClick={onSubmit} loading={submit.isPending}>
                Check stock + raise PR
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Result">
            {!result && <Text type="secondary">Run the check to see the decision.</Text>}
            {result && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <ProcurementStatusBadge status={result.decision} />
                {result.pr_no && (
                  <Text>PR raised: <Text strong>{result.pr_no}</Text></Text>
                )}
                {result.link_id && (
                  <Text type="secondary">
                    Link id: <code>{result.link_id.slice(0, 8)}</code>
                  </Text>
                )}
                <Text>Shortage qty: <Text strong>{result.shortage_qty.toFixed(2)}</Text></Text>
                <Text>Projected stock: <Text strong>{result.projected_stock.toFixed(2)}</Text></Text>
                {result.status && (
                  <Space>
                    <Text>Link status:</Text>
                    <ProcurementStatusBadge status={result.status} />
                  </Space>
                )}
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
